/**
 * TransformationStepResult Component
 *
 * Displays a single transformation step result with status, metrics, and error details.
 */

import React, { useState } from "react";
import type { TransformationResult, TransformationStepStatus } from "../../../types/elt-job-execution.types";

interface TransformationStepResultProps {
  step: TransformationResult;
  index: number;
}

const statusConfig: Record<TransformationStepStatus, { label: string; className: string; iconColor: string }> = {
  completed: {
    label: "Completed",
    className: "bg-green-50 border-green-200",
    iconColor: "text-green-600",
  },
  failed: {
    label: "Failed",
    className: "bg-red-50 border-red-200",
    iconColor: "text-red-600",
  },
  pending: {
    label: "Pending",
    className: "bg-gray-50 border-gray-200",
    iconColor: "text-gray-400",
  },
  running: {
    label: "Running",
    className: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
  },
  skipped: {
    label: "Skipped",
    className: "bg-yellow-50 border-yellow-200",
    iconColor: "text-yellow-600",
  },
};

const StatusIcon: React.FC<{ status: TransformationStepStatus }> = ({ status }) => {
  const config = statusConfig[status];

  if (status === "completed") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }

  if (status === "failed") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }

  if (status === "running") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor} animate-spin`} fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );
  }

  if (status === "skipped") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
      </svg>
    );
  }

  // pending
  return (
    <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="10" strokeWidth={2} />
    </svg>
  );
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

export const TransformationStepResult: React.FC<TransformationStepResultProps> = ({ step, index }) => {
  const config = statusConfig[step.status];
  const [showSql, setShowSql] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const hasErrorDetails = step.errorDetails && Object.keys(step.errorDetails).length > 0;
  const hasFailedSql = step.failedSql && step.failedSql.length > 0;

  return (
    <div className={`border rounded-lg p-3 ${config.className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-mono w-5">#{index + 1}</span>
          <StatusIcon status={step.status} />
          <span className="font-medium text-gray-900">{step.name}</span>
        </div>
        <span className={`text-xs font-medium uppercase ${config.iconColor}`}>{config.label}</span>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6M12 9v6" />
          </svg>
          {step.rowsAffected.toLocaleString()} rows
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {formatDuration(step.durationMs)}
        </span>
      </div>

      {step.error && (
        <div className="mt-2 p-2 bg-white bg-opacity-60 rounded border border-red-200">
          <p className="text-xs font-medium text-red-700 mb-1">Error:</p>
          <pre className="text-xs text-red-600 whitespace-pre-wrap font-mono overflow-x-auto">{step.error}</pre>
        </div>
      )}

      {/* PostgreSQL Error Details */}
      {hasErrorDetails && (
        <div className="mt-2">
          <button onClick={() => setShowErrorDetails(!showErrorDetails)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1">
            <svg
              className={`w-3 h-3 transition-transform ${showErrorDetails ? "rotate-90" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showErrorDetails ? "Hide" : "Show"} PostgreSQL Error Details
          </button>
          {showErrorDetails && (
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-xs">
              <dl className="space-y-1">
                {step.errorDetails!.code && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">SQLSTATE:</dt>
                    <dd className="text-red-700 font-mono">{step.errorDetails!.code}</dd>
                  </div>
                )}
                {step.errorDetails!.detail && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">Detail:</dt>
                    <dd className="text-red-700">{step.errorDetails!.detail}</dd>
                  </div>
                )}
                {step.errorDetails!.hint && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">Hint:</dt>
                    <dd className="text-red-700">{step.errorDetails!.hint}</dd>
                  </div>
                )}
                {step.errorDetails!.position && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">Position:</dt>
                    <dd className="text-red-700">Character {step.errorDetails!.position}</dd>
                  </div>
                )}
                {step.errorDetails!.constraint && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">Constraint:</dt>
                    <dd className="text-red-700 font-mono">{step.errorDetails!.constraint}</dd>
                  </div>
                )}
                {step.errorDetails!.table && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">Table:</dt>
                    <dd className="text-red-700 font-mono">{step.errorDetails!.table}</dd>
                  </div>
                )}
                {step.errorDetails!.column && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-red-800">Column:</dt>
                    <dd className="text-red-700 font-mono">{step.errorDetails!.column}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Failed SQL Statement */}
      {hasFailedSql && (
        <div className="mt-2">
          <button onClick={() => setShowSql(!showSql)} className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1">
            <svg className={`w-3 h-3 transition-transform ${showSql ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showSql ? "Hide" : "Show"} Failed SQL Statement
          </button>
          {showSql && (
            <div className="mt-2">
              <pre className="text-xs bg-gray-800 text-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
                {step.failedSql}
              </pre>
              {step.sqlTruncated && <p className="text-xs text-gray-500 mt-1 italic">SQL was truncated due to length limits (5000 characters max)</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
